/* ==========================================================================
   RIZQ PREMIUM ENGINE — interactions
   Zero dependencies. Loaded with `defer` by sections/rizq-foundation.liquid.
   Modules:
     1. reveal   — IntersectionObserver scroll reveals with stagger
     2. parallax — subtle translate on [data-rizq-parallax] media
     3. stickyAtc— sticky add-to-cart bar on product pages (proxies the
                   real form button, so variants & cart logic stay intact)
     4. marquee  — normalizes marquee speed to content width
   Everything no-ops when the user prefers reduced motion, and the page is
   fully usable with JavaScript disabled (we only ever ADD enhancements).
   ========================================================================== */
(function () {
  'use strict';

  var cfgEl = document.querySelector('[data-rizq-config]');
  var cfg = {
    reveal: !cfgEl || cfgEl.getAttribute('data-reveal') !== 'false',
    stickyAtc: !cfgEl || cfgEl.getAttribute('data-sticky-atc') !== 'false'
  };
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     1. SCROLL REVEALS
     Auto-instruments common elements + anything marked [data-rizq-reveal].
     Classes are only added here, so no-JS visitors see everything.
     ------------------------------------------------------------------ */
  function initReveal() {
    if (reducedMotion || !cfg.reveal || !('IntersectionObserver' in window)) return;

    var targets = [];
    document.querySelectorAll('.title-wrapper-with-link, .shopify-section > .rich-text, .collection-hero__text-wrapper').forEach(function (el) { targets.push([el, 0]); });
    document.querySelectorAll('.grid').forEach(function (grid) {
      var cards = grid.querySelectorAll(':scope > .grid__item');
      cards.forEach(function (item, i) { targets.push([item, (i % 4) * 80]); });
    });
    document.querySelectorAll('[data-rizq-reveal]').forEach(function (el) {
      targets.push([el, parseInt(el.getAttribute('data-rizq-reveal'), 10) || 0]);
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('rizq-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    targets.forEach(function (pair) {
      var el = pair[0], delay = pair[1];
      if (el.classList.contains('rizq-reveal')) return;
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.6 && r.bottom > 0) return; // above the fold — skip
      el.classList.add('rizq-reveal');
      el.style.setProperty('--rizq-d', delay + 'ms');
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     2. PARALLAX — media inside [data-rizq-parallax] drifts slightly
     ------------------------------------------------------------------ */
  function initParallax() {
    if (reducedMotion) return;
    var els = document.querySelectorAll('[data-rizq-parallax]');
    if (!els.length) return;
    var active = new Set();
    var gate = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.isIntersecting ? active.add(e.target) : active.delete(e.target); });
    });
    els.forEach(function (el) { gate.observe(el); });

    var ticking = false;
    function frame() {
      active.forEach(function (el) {
        var strength = parseFloat(el.getAttribute('data-rizq-parallax')) || 14;
        var r = el.getBoundingClientRect();
        var progress = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        var media = el.querySelector('img, video') || el;
        media.style.transform = 'translate3d(0,' + (progress * -strength).toFixed(1) + 'px,0) scale(1.06)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking && active.size) { ticking = true; requestAnimationFrame(frame); }
    }, { passive: true });
    requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------------
     3. STICKY ADD-TO-CART (product pages)
     Appears once the native buy buttons scroll above the viewport.
     IMPORTANT: the theme re-renders the buy-button subtree on variant
     change, so we never hold references — the current elements are
     re-queried on every scroll frame and click.
     ------------------------------------------------------------------ */
  function initStickyAtc() {
    if (!cfg.stickyAtc) return;
    if (!document.querySelector('.product-form__buttons')) return;
    var title = document.querySelector('.product__title h1, .product__info-container h1');
    if (!title) return;

    var bar = document.createElement('div');
    bar.className = 'rizq-satc';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML =
      '<div class="rizq-satc__meta">' +
        '<p class="rizq-satc__title"></p>' +
        '<p class="rizq-satc__price"></p>' +
      '</div>' +
      '<button type="button" class="rizq-satc__btn"></button>';
    document.body.appendChild(bar);

    var barTitle = bar.querySelector('.rizq-satc__title');
    var barPrice = bar.querySelector('.rizq-satc__price');
    var barBtn = bar.querySelector('.rizq-satc__btn');
    barTitle.textContent = title.textContent.trim();

    function realButton() { return document.querySelector('.product-form__submit'); }

    function syncFromForm() {
      var priceEl = document.querySelector('.product__info-container .price__regular .price-item--regular, .product__info-container .price-item--sale');
      if (priceEl) barPrice.textContent = priceEl.textContent.trim();
      var btn = realButton();
      if (!btn) return;
      var soldOut = btn.disabled || btn.getAttribute('aria-disabled') === 'true';
      barBtn.disabled = soldOut;
      var label = (btn.querySelector('span') || btn).textContent.trim();
      barBtn.textContent = label || 'Add to cart';
    }
    syncFromForm();

    var info = document.querySelector('.product__info-container');
    if (info && 'MutationObserver' in window) {
      new MutationObserver(function () { syncFromForm(); }).observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }

    barBtn.addEventListener('click', function () {
      var btn = realButton();
      if (btn) btn.click();
      barBtn.blur();
    });

    var shown = false;
    function show(state) {
      if (state === shown) return;
      shown = state;
      bar.classList.toggle('rizq-satc--show', state);
      bar.setAttribute('aria-hidden', String(!state));
    }
    var ticking = false;
    function check() {
      var bb = document.querySelector('.product-form__buttons'); // re-query: node is replaced on variant change
      if (bb) show(bb.getBoundingClientRect().bottom < 0);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(check); }
    }, { passive: true });
    check();
  }

  /* ------------------------------------------------------------------
     4. MARQUEE speed normalization — consistent px/s regardless of length
     ------------------------------------------------------------------ */
  function initMarquee() {
    document.querySelectorAll('.rizq-marquee').forEach(function (m) {
      var track = m.querySelector('.rizq-marquee__track');
      if (!track) return;
      var speed = parseFloat(m.getAttribute('data-speed')) || 60;
      var width = track.scrollWidth / 2;
      m.style.setProperty('--rizq-marquee-duration', Math.max(8, width / speed) + 's');
    });
  }

  function init() {
    try { initReveal(); } catch (e) { /* never break the storefront */ }
    try { initParallax(); } catch (e) {}
    try { initStickyAtc(); } catch (e) {}
    try { initMarquee(); } catch (e) {}
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () { initMarquee(); });
})();
